import { Box, Group, Stack } from '@mantine/core';
import * as React from 'react';
import { ReactNode, useRef } from 'react';
import { createPortal } from 'react-dom';

import { ScrollDown } from '@/core/components/RootLayout/Sidebar/components/ScrollDown';
import { ScrollUp } from '@/core/components/RootLayout/Sidebar/components/ScrollUp';

import { SidebarItem, useContextSidebarType, useSidebarItems, useSidebarState } from './hooks/useSidebar';

interface ContextSidebarProps {
  items: SidebarItem[];
  sidebarHeader?: ReactNode;
  sidebarFooter?: ReactNode;
  navbarFooter?: ReactNode;

  baseUrl: string;
}

interface LinkBarProps {
  links: ReactNode;
}

function Sidebar({ links }: LinkBarProps) {
  const ref = useRef<HTMLDivElement>(null);

  const onScrollDown = () => {
    if (ref.current) {
      ref.current.scroll({
        top: ref.current.scrollTop + 60,
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  const onScrollUp = () => {
    if (ref.current) {
      ref.current.scroll({
        top: ref.current.scrollTop - 60,
        left: 0,
        behavior: 'smooth',
      });
    }
  };

  return (
    <Box
      pos="relative"
      mah="100vh"
      h="100vh"
    >
      <ScrollUp onScrollUp={onScrollUp} />
      <Stack
        ref={ref}
        style={{ overflowY: 'auto', height: '94vh', paddingTop: '40px' }}
      >
        {links}
      </Stack>
      <ScrollDown onScrollDown={onScrollDown} />
    </Box>
  );
}

function Navbar({ links }: LinkBarProps) {
  return (
    <Group
      pb="xs"
      wrap="nowrap"
      bg="var(--mantine-color-sidebar-context-filled)"
      style={{ overflowX: 'auto', whiteSpace: 'nowrap' }}
    >
      {links}
    </Group>
  );
}

export function ContextSidebar({ items, sidebarHeader, sidebarFooter, navbarFooter, baseUrl }: ContextSidebarProps) {
  const [domReady, setDomReady] = React.useState(false);
  const { isMainActive, setMainActive, setHasSidebarContext } = useSidebarState();
  const type = useContextSidebarType();
  const links = useSidebarItems({
    baseUrl,
    items,
    header: type === 'sidebar' ? sidebarHeader : null,
    footer: type === 'sidebar' ? sidebarFooter : null,
    isCollapsed: type === 'sidebar' ? isMainActive : false,
  });

  React.useEffect(() => {
    setDomReady(true);
    setMainActive(type != 'sidebar');

    return () => {
      setMainActive(true);
      setHasSidebarContext(false);
    };
  }, [type, setMainActive, setHasSidebarContext]);

  return domReady ? (
    <>
      {createPortal(
        type == 'sidebar' ? <Sidebar links={links} /> : <Navbar links={links} />,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        document.getElementById(type == 'sidebar' ? 'context-sidebar' : 'context-navbar')!,
      )}
      {type === 'navbar' &&
        navbarFooter !== null &&
        createPortal(
          <>{navbarFooter}</>,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          document.getElementById('context-footer')!,
        )}
    </>
  ) : null;
}
